/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
import {
  ActionIcon,
  Box,
  Button,
  Group,
  List,
  ScrollAreaAutosize,
  Space,
  Stack,
  Text,
  Title,
  rem,
} from '@mantine/core';
import {
  closeAllModals,
  closeModal,
  type ContextModalProps,
} from '@mantine/modals';
import dayjs from 'dayjs';
import { ClockIcon, DotIcon, XIcon } from 'lucide-react';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { useQueryState, parseAsString, parseAsArrayOf } from 'nuqs';
import { usePromotionEstimate } from '../../hooks/use-promotion-estimate';
import LoadingPromo from './loading-promo';
import { ModalName, showModal } from '@/components/modals';
import { usePromoDetail } from '@/features/booking/hooks/use-booking-promotion';
import { assetPrefix } from '@/lib/constants';
import useIsMobile from '@/lib/is-mobile/use-is-mobile';
import { useLocaleSuffix } from '@/navigation';
import { useBookingStore } from '@/stores/booking/booking-store-provider';

type PromoDetailProps = {
  promo_id: string;
  valid: boolean;
};

const PromoImage = ({ imgUrl }: { imgUrl: string }) => (
  <Box style={{ width: '100%', height: '225px', position: 'relative' }}>
    <Image
      src={imgUrl}
      alt="detail-default"
      objectFit="cover"
      width={440}
      height={225}
      className="h-56 w-full bg-top object-cover"
      loading="lazy"
      onError={() => `${assetPrefix}/static/images/default-promo-detail.webp`}
    />
  </Box>
);

const PromoContent = ({ promo, localeSuffix, tBooking }) => {
  const isMobile = useIsMobile();
  return (
    <Stack styles={{ root: { flex: 'auto' } }} p={24} gap={8}>
      <Text fz={10} fw={600}>
        Ahamove
      </Text>
      <Title order={3}>
        {promo?.description_vi_vn || promo?.description || promo?._id}
      </Title>
      <Group gap={5}>
        <ClockIcon size={16} />
        <Text fz={12} fw={500} c={'neutral.6'}>
          {tBooking('promo.expires_on')}{' '}
          {dayjs(promo?.end_time * 1000).format('HH:mm  DD/MM/YYYY')}
        </Text>
      </Group>
      {(promo?.content_vi_vn || promo?.content_en_us) && (
        <Box mx={-24}>
          <Title px={24} order={4} pt={8}>
            {tBooking('promo.about_promo_code')}
          </Title>
          <ScrollAreaAutosize
            type="hover"
            styles={{
              root: {
                width: '100%',
                maxHeight: isMobile ? '100%' : 140,
              },
            }}
          >
            <List pt={2} px={24} icon={<DotIcon size={16} />} size="sm">
              <List.Item>
                <div
                  dangerouslySetInnerHTML={{
                    __html: promo[`content_${localeSuffix}`],
                  }}
                />
              </List.Item>
            </List>
          </ScrollAreaAutosize>
        </Box>
      )}
    </Stack>
  );
};

const PromoButton = ({
  promo,
  innerProps,
  isMobile,
  tBooking,
  isFetchPromoDetailError,
}) => {
  const [urlPromoIds, setUrlPromoIds] = useQueryState(
    'promo_code',
    parseAsArrayOf(parseAsString).withDefault([])
  );
  const { errorPromo, setErrorPromo, setIsSuggestPromotion } = useBookingStore(
    (state) => state
  );

  const { applySinglePromoCode } = usePromotionEstimate();

  const handleUsePromo = () => {
    applySinglePromoCode(innerProps.promo_id, urlPromoIds);
    closeAllModals();
  };

  const handleUseLater = () => {
    setIsSuggestPromotion(false);
    setErrorPromo(null);

    // Remove the current promo ID from the URL parameters
    const newPromoIds = urlPromoIds.filter((id) => id !== innerProps.promo_id);
    void setUrlPromoIds(newPromoIds);
    showModal(
      ModalName.Promotion,
      {},
      {
        fullScreen: isMobile,
        centered: true,
        closeOnClickOutside: !errorPromo,
        padding: 0,
        styles: {
          root: { background: '#fff', padding: 0 },
          header: { background: '#fff', padding: '0px 24px' },
          content: { background: '#fff', padding: '0px' },
          body: { padding: '0px', background: '#fff' },
        },
      }
    );
    closeModal(ModalName.PromotionDetail);
  };

  const isCurrentPromo =
    urlPromoIds.includes(promo?._id || '') || isFetchPromoDetailError;

  return (
    <Box px={16} pb={16} pt={8} style={{ flex: 'none' }}>
      <Button
        fullWidth
        color={isCurrentPromo ? 'red.2' : 'primary'}
        c={isCurrentPromo ? 'red.6' : 'white'}
        h={rem(48)}
        fw={700}
        onClick={isCurrentPromo ? handleUseLater : handleUsePromo}
      >
        {isCurrentPromo
          ? tBooking('promo.button_use_later')
          : tBooking('promo.button_use_now')}
      </Button>
    </Box>
  );
};

export default function PromoDetail({
  innerProps,
}: ContextModalProps<PromoDetailProps>) {
  const isMobile = useIsMobile();
  const tBooking = useTranslations('booking');
  const localeSuffix = useLocaleSuffix();
  const { promo, isFetching, imgUrl, isError } = usePromoDetail(
    innerProps.promo_id
  );

  if (isFetching) return <LoadingPromo />;

  return (
    <Box
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 0,
        height: '100%',
        justifyContent: innerProps.valid ? 'space-between' : 'flex-start',
        flex: '1 1 auto',
        backgroundColor: '#fff',
      }}
    >
      <PromoImage imgUrl={imgUrl} />
      <ActionIcon
        variant="light"
        size={40}
        color="neutral"
        style={{
          borderRadius: '50%',
          position: 'absolute',
          top: 16,
          right: 16,
          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
          backgroundColor: '#fff',
        }}
        onClick={() => closeModal(ModalName.PromotionDetail)}
      >
        <XIcon size={24} />
      </ActionIcon>
      {promo?._id && (
        <PromoContent
          promo={promo}
          localeSuffix={localeSuffix}
          tBooking={tBooking}
        />
      )}
      {innerProps.valid ? (
        <PromoButton
          promo={promo}
          innerProps={innerProps}
          isMobile={isMobile}
          tBooking={tBooking}
          isFetchPromoDetailError={isError}
        />
      ) : (
        <Space h={42} />
      )}
    </Box>
  );
}
